import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';

const SafetySection = () => {
  const [ref, isInView] = useInView({ threshold: 0.2 });

  const safetyFeatures = [
    {
      icon: 'ri-shield-check-line',
      title: 'ISO Certified',
      description: 'International safety and quality management standards',
    },
    {
      icon: 'ri-user-star-line',
      title: 'Trained Operators',
      description: 'Highly skilled and certified equipment operators',
    },
    {
      icon: 'ri-tools-line',
      title: 'Regular Maintenance',
      description: 'Comprehensive equipment inspection and maintenance',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Safety Protocols',
      description: 'Strict adherence to Saudi safety regulations',
    },
  ];

  return (
    <section id="safety" className="py-24 bg-white">
      <div className="container mx-auto px-4 lg:px-8" ref={ref}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 bg-accent rounded-full"></div>
              <span className="text-accent font-inter font-semibold text-sm uppercase tracking-wider">Our Commitment</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-poppins font-black text-primary mb-6">
              Safety & Quality First
            </h2>
            
            <p className="text-neutral-600 font-inter text-base leading-relaxed mb-8">
              At Rawafi Al Mutahidun, safety is not just a priority—it's our foundation. 
              We maintain the highest standards of safety and quality in every project, 
              ensuring the well-being of our team and the success of your construction goals.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {safetyFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${feature.icon} text-2xl text-accent`}></i>
                  </div>
                  <div>
                    <h3 className="font-poppins font-bold text-primary text-base mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-neutral-600 font-inter text-sm">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative h-[600px] rounded-3xl overflow-hidden"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/210aefa78647600235026810e0173084.jpg"
              alt="Safety Standards"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
            
            {/* Safety Badge */}
            <div className="absolute bottom-8 left-8 right-8">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-3xl text-white"></i>
                  </div>
                  <div>
                    <div className="font-poppins font-black text-3xl text-primary">100%</div>
                    <div className="text-neutral-600 font-inter text-sm">Safety Compliance</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SafetySection;
